import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import React from "react";
import { ApiError } from "../../../app/api/client";
import { HTTP_STATUS_BAD_REQUEST,HTTP_STATUS_FORBIDDEN } from "../../../app/constants/httpStatus";
import { isMissingEntryError } from "../../../../../../../shared/frontend/navigation/missingEntry";
import { emitPersonsChanged } from "../../../app/persons/personsEvents";
import { globalKey } from "../../../app/query/queryKeys";
import { getBankDataFromIban } from "../../../app/utils/ibanLookup";
import { isValidIban } from "../../../app/utils/sepa";
import type { PersonDto } from "../types";
import type { PersonFormValues } from "./personDetailTypes";
import { parseDateOnly } from "./personDetailUtils";
import { buildUpsertPersonDtoFromForm,buildUpsertPersonDtoFromPerson } from "./personUpsertDto";
import { useFormDirty } from "../../../app/utils/useFormDirty";

export type ApiPersonDetailDto = Omit<PersonDto, "phone" | "mobile"> & {
  phone?: unknown;
  mobile?: unknown;
};

export type PersonDetailApiClient = {
  get<T>(url: string, opts?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
  put<T = unknown>(url: string, body?: unknown, opts?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
  req<T>(
    url: string,
    opts?: RequestInit & { suppressGlobalErrorToast?: boolean; tenantId?: string }
  ): Promise<T>;
};

type BlockedLoadReason = "forbidden" | "missing";

type UsePersonDetailRecordArgs = {
  api: PersonDetailApiClient;
  personId: string;
  form: FormInstance<PersonFormValues>;
  canReadCheckins: boolean;
  onBlockedLoad: (reason: BlockedLoadReason) => void;
};

type SavePersonOptions = {
  bankName: string | null;
  canChangeHomeStudio: boolean;
};

type ChangeHomeStudioOptions = {
  refreshPerson?: boolean;
};

function nullableContactText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  return text ? text : null;
}

/**
 * Telefon- und Mobilnummern kommen je nach Quelle als Zahl oder mit Leerraum. Jeder Schreibvorgang
 * in den Personen-Cache muss hier durch — der Cache speist auch das Profilformular, und dort wäre
 * eine Zahl im Textfeld ein stiller Datenfehler beim nächsten Speichern.
 */
export function normalizePersonDetailDto(dto: ApiPersonDetailDto): PersonDto {
  return {
    ...dto,
    phone: nullableContactText(dto.phone),
    mobile: nullableContactText(dto.mobile)
  };
}

function buildPersonFormValues(person: PersonDto): PersonFormValues {
  return {
    firstName: person.firstName || "",
    lastName: person.lastName || "",
    title: person.title ?? null,
    useDu: person.useDu ?? null,
    gender: person.gender ?? null,
    dateOfBirth: parseDateOnly(person.dateOfBirth),
    email: person.email ?? null,
    phone: person.phone ?? null,
    mobile: person.mobile ?? null,
    street: person.street ?? null,
    zip: person.zip ?? null,
    city: person.city ?? null,
    country: person.country ?? null,
    bankAccountHolder: person.bankAccountHolder ?? null,
    iban: person.iban ?? null,
    bic: person.bic ?? null,
    bankName: person.bankName ?? null,
    mandateReference: person.mandateReference ?? null,
    mandateSignedDate: parseDateOnly(person.mandateSignedDate),
    homeStudioId: person.homeStudioId ?? null,
    doNotContactMessages: person.doNotContactMessages === true,
    doNotContactCalls: person.doNotContactCalls === true
  };
}

function shouldLoadActiveCheckIn(person: PersonDto, canReadCheckins: boolean): boolean {
  if (!canReadCheckins) return false;
  return person.hideProspectDetailSections !== true;
}

export async function loadPersonDetailRecord(api: Pick<PersonDetailApiClient, "req">, personId: string): Promise<PersonDto> {
  const dto = await api.req<ApiPersonDetailDto>(`/api/v1/persons/${encodeURIComponent(personId)}`, {
    method: "GET",
    suppressGlobalErrorToast: true
  });
  return normalizePersonDetailDto(dto);
}

async function loadActiveCheckInAt(api: PersonDetailApiClient, personId: string): Promise<string | null> {
  const active =
    (await api
      .get<Array<{ personId: string; studioId?: string | null; checkedInAt: string }>>(
        `/api/v1/checkins/active?personId=${encodeURIComponent(personId)}`
      )) || [];
  const activeCheckIn = active[0] || null;
  return activeCheckIn?.checkedInAt ? String(activeCheckIn.checkedInAt) : null;
}

async function resolveBankName(api: PersonDetailApiClient, person: PersonDto): Promise<string | null> {
  if (person.bankName) return person.bankName;
  if (!person.iban || !isValidIban(person.iban)) return null;
  const data = await getBankDataFromIban(person.iban, api).catch(() => null);
  return data?.bankName || null;
}

export function usePersonDetailRecord({
  api,
  personId,
  form,
  canReadCheckins,
  onBlockedLoad
}: UsePersonDetailRecordArgs) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = React.useState(false);
  const { dirty, initialize, sync, markSaved, resetDirty } = useFormDirty(form);
  const initializedPersonId = React.useRef<string | null>(null);

  const personKey = React.useMemo(() => globalKey("persons", personId), [personId]);

  const personQuery = useQuery({
    queryKey: personKey,
    queryFn: () => loadPersonDetailRecord(api, personId),
    enabled: !!personId,
    // Ein 403 oder 404 ist eine Aussage über die Person, kein Netzproblem — Wiederholen hilft nicht
    // und verzögert nur die Meldung an den Aufrufer.
    retry: false
  });

  const person = personQuery.data ?? null;
  const loadError = personQuery.error;
  const blockedReason: BlockedLoadReason | null =
    loadError instanceof ApiError && loadError.status === HTTP_STATUS_FORBIDDEN
      ? "forbidden"
      : loadError instanceof ApiError &&
          (isMissingEntryError(loadError) || loadError.status === HTTP_STATUS_BAD_REQUEST)
        ? "missing"
        : null;
  const personDetailFetchError = !!loadError && blockedReason === null;

  // Der Aufrufer entscheidet, was bei „gibt es nicht" bzw. „darfst du nicht" passiert (Umleitung,
  // Hinweis). Das ist eine Wirkung nach außen und gehört deshalb in einen Effekt, nicht in den
  // Render-Durchlauf.
  const onBlockedLoadRef = React.useRef(onBlockedLoad);
  React.useEffect(() => {
    onBlockedLoadRef.current = onBlockedLoad;
  }, [onBlockedLoad]);
  React.useEffect(() => {
    if (blockedReason) onBlockedLoadRef.current(blockedReason);
  }, [blockedReason]);

  // antd-Formulare sind imperativ: die geladenen Werte müssen hineingeschrieben werden.
  React.useEffect(() => {
    if (!person) return;
    if (initializedPersonId.current !== personId) {
      initialize(buildPersonFormValues(person));
      initializedPersonId.current = personId;
    } else {
      sync(buildPersonFormValues(person));
    }
  }, [initialize, person, personId, sync]);

  const bankNameQuery = useQuery({
    queryKey: globalKey("persons", personId, "bank-name", person?.iban ?? null, person?.bankName ?? null),
    queryFn: () => resolveBankName(api, person!),
    enabled: !!person,
    retry: false
  });
  const personBankName = bankNameQuery.data ?? null;

  React.useEffect(() => {
    if (!bankNameQuery.isSuccess || dirty) return;
    form.setFieldValue("bankName", personBankName || "");
    resetDirty();
  }, [bankNameQuery.isSuccess, dirty, form, personBankName, resetDirty]);

  const checkInQuery = useQuery({
    queryKey: globalKey("check-ins", "active", personId),
    queryFn: () => loadActiveCheckInAt(api, personId),
    enabled: !!person && shouldLoadActiveCheckIn(person, canReadCheckins),
    retry: false
  });
  const activeCheckInAt = checkInQuery.data ?? null;

  const load = React.useCallback(async () => {
    await personQuery.refetch();
  }, [personQuery]);

  const refreshPerson = React.useCallback(async () => {
    const refreshed = await queryClient.fetchQuery({
      queryKey: personKey,
      queryFn: () => loadPersonDetailRecord(api, personId)
    });
    return refreshed;
  }, [api, personId, personKey, queryClient]);

  const setPerson = React.useCallback(
    (nextPerson: PersonDto | null) => {
      queryClient.setQueryData(personKey, nextPerson ? normalizePersonDetailDto(nextPerson) : undefined);
    },
    [personKey, queryClient]
  );

  const setPersonBankName = React.useCallback(
    (nextBankName: string | null) => {
      queryClient.setQueryData(
        globalKey("persons", personId, "bank-name", person?.iban ?? null, person?.bankName ?? null),
        nextBankName
      );
    },
    [person?.bankName, person?.iban, personId, queryClient]
  );

  const savePerson = React.useCallback(
    async ({ bankName, canChangeHomeStudio }: SavePersonOptions) => {
      if (!person) return false;
      try {
        const values = await form.validateFields();
        const savedSnapshot = form.getFieldsValue(true);
        setSaving(true);
        const dto = buildUpsertPersonDtoFromForm(values, person, { bankName, canChangeHomeStudio });
        await api.put(`/api/v1/persons/${encodeURIComponent(personId)}`, dto);
        markSaved(savedSnapshot);
        emitPersonsChanged({ reason: "updated", personId: String(personId) });
        await refreshPerson();
        await checkInQuery.refetch();
        return true;
      } catch {
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, checkInQuery, form, markSaved, person, personId, refreshPerson]
  );

  const changeHomeStudio = React.useCallback(
    async (homeStudioId: string, options: ChangeHomeStudioOptions = {}) => {
      if (!person) return null;
      try {
        setSaving(true);
        const dto = buildUpsertPersonDtoFromPerson(person, { homeStudioId });
        await api.put(`/api/v1/persons/${encodeURIComponent(personId)}`, dto);
        emitPersonsChanged({ reason: "updated", personId: String(personId) });
        if (options.refreshPerson) return await refreshPerson();
        await load();
        return null;
      } catch {
        return null;
      } finally {
        setSaving(false);
      }
    },
    [api, load, person, personId, refreshPerson]
  );

  return {
    person,
    setPerson,
    personDetailFetchError,
    personBankName,
    setPersonBankName,
    // Erstaufbau, **nicht** `isFetching`: Die Seite legt darüber ein `LoadingOverlay scope="block"`,
    // und das sperrt die Fläche. Mit `isFetching` wäre die Personenakte bei jedem
    // Hintergrund-Neuladen — Fensterfokus, Invalidierung nach dem Speichern — kurz unbedienbar.
    loading: personQuery.isPending,
    activeCheckInAt,
    saving,
    dirty,
    savePerson,
    changeHomeStudio,
    refreshPerson,
    load
  };
}
